import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  pageTitleClass,
  pageSubtitleClass,
  cardClass,
  cardHeaderClass,
  cardTitleClass,
  cardBodyClass,
  btnPrimaryClass,
  btnSecondaryClass,
  alertErrorClass,
  badgeClass,
  statsGridClass,
  spinnerClass,
} from "../../dashboardStyles";
import { useMyBookings } from "./useMyBookings";
import {
  formatDate,
  formatTime,
  statusBadgeClass,
  daysUntil,
} from "./bookingPresentation";

const StatTile = ({ icon, value, label }) => (
  <div className={cardClass}>
    <div className="flex items-center gap-4 p-5">
      <div className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
        <i className={`bi ${icon} text-xl`} aria-hidden="true" />
      </div>
      <div className="min-w-0">
        <p className="m-0 text-2xl font-bold text-ink-900">{value}</p>
        <p className="m-0 text-xs font-medium text-ink-500">{label}</p>
      </div>
    </div>
  </div>
);

/**
 * The customer's landing page: what's next, and just enough context to be
 * useful.
 *
 * Scoped tightly on purpose — a customer is not running a business, so there
 * are no revenue charts or org-wide totals here. The next appointment is the
 * whole reason they signed in, so it leads and everything else supports it.
 */
const CustomerOverview = () => {
  const { t, i18n } = useTranslation();
  const { loading, error, upcoming, next, byService, totalCount, placesCount } =
    useMyBookings();

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div
          role="status"
          aria-label={t("Dashboard.Overview.loading")}
          className={spinnerClass}
        />
      </div>
    );
  }

  const days = next ? daysUntil(next.bookingDateTime) : null;
  const countdown =
    days === 0
      ? t("Dashboard.MyBookings.today")
      : days === 1
        ? t("Dashboard.MyBookings.tomorrow")
        : t("Dashboard.MyBookings.inDays", { count: days });

  return (
    <div>
      <div className="mb-6">
        <h1 className={pageTitleClass}>{t("Dashboard.Overview.title")}</h1>
        <p className={pageSubtitleClass}>{t("Dashboard.Overview.subtitle")}</p>
      </div>

      {error && (
        <div className={alertErrorClass} role="alert">
          <i className="bi bi-exclamation-circle mt-0.5 shrink-0" />
          {t("Dashboard.MyBookings.errors.loadFailed")}
        </div>
      )}

      {/* Next appointment — the headline. */}
      {next ? (
        <div className="mb-6 overflow-hidden rounded-card border border-brand-200 bg-brand-50 shadow-card">
          <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
            <div className="min-w-0">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold tracking-wide text-brand-700 uppercase">
                  {t("Dashboard.Overview.nextAppointment")}
                </span>
                <span className={`${badgeClass} bg-brand-600 text-white`}>
                  {countdown}
                </span>
              </div>
              <p className="m-0 text-xl font-bold text-ink-900 sm:text-2xl">
                {next.serviceName}
              </p>
              <p className="mt-1 mb-0 text-fluid-sm text-ink-600">
                {formatDate(next.bookingDateTime, i18n.language)}
                {" · "}
                {formatTime(next.bookingDateTime, i18n.language)}
                {" · "}
                {next.organizationName}
              </p>
            </div>
            <Link
              to="/dashboard/my-bookings"
              className={`${btnPrimaryClass} shrink-0`}
            >
              {t("Dashboard.Overview.viewDetails")}
              <i className="bi bi-arrow-right" aria-hidden="true" />
            </Link>
          </div>
        </div>
      ) : (
        <div className={`${cardClass} mb-6`}>
          <div className={`${cardBodyClass} py-10 text-center`}>
            <i className="bi bi-calendar-plus mb-3 block text-4xl text-ink-300" />
            <p className="m-0 mb-4 text-fluid-sm text-ink-500">
              {t("Dashboard.Overview.noUpcoming")}
            </p>
            <Link to="/dashboard/my-bookings" className={btnSecondaryClass}>
              {t("Dashboard.Overview.viewHistory")}
            </Link>
          </div>
        </div>
      )}

      <div className={`${statsGridClass} mb-6`}>
        <StatTile
          icon="bi-calendar-check"
          value={upcoming.length}
          label={t("Dashboard.Overview.stats.upcoming")}
        />
        <StatTile
          icon="bi-clock-history"
          value={totalCount}
          label={t("Dashboard.Overview.stats.total")}
        />
        <StatTile
          icon="bi-shop"
          value={placesCount}
          label={t("Dashboard.Overview.stats.places")}
        />
      </div>

      <div className="grid gap-6 [grid-template-columns:repeat(auto-fit,minmax(min(320px,100%),1fr))]">
        {/* What kind of appointments this customer books. */}
        <div className={cardClass}>
          <div className={cardHeaderClass}>
            <h3 className={cardTitleClass}>
              {t("Dashboard.Overview.appointmentTypes")}
            </h3>
          </div>
          <div className={cardBodyClass}>
            {byService.length === 0 ? (
              <p className="m-0 text-fluid-sm text-ink-500">
                {t("Dashboard.Overview.noData")}
              </p>
            ) : (
              <ul className="m-0 flex list-none flex-col gap-3 p-0">
                {byService.map(({ name, count }) => (
                  <li key={name}>
                    <div className="mb-1 flex items-center justify-between gap-3">
                      <span className="min-w-0 truncate text-fluid-sm font-medium text-ink-900">
                        {name}
                      </span>
                      <span className="shrink-0 text-xs font-semibold text-ink-500">
                        {count}
                      </span>
                    </div>
                    {/* Share of this customer's own bookings — a proportion of
                        a handful of appointments, so the bar is a visual aid,
                        not a statistic. */}
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-ink-100">
                      <div
                        className="h-full rounded-full bg-brand-600"
                        style={{ width: `${(count / totalCount) * 100}%` }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className={cardClass}>
          <div className={cardHeaderClass}>
            <h3 className={cardTitleClass}>
              {t("Dashboard.Overview.comingUp")}
            </h3>
            <Link
              to="/dashboard/my-bookings"
              className="text-xs font-semibold text-brand-600 hover:text-brand-700"
            >
              {t("Dashboard.Overview.seeAll")}
            </Link>
          </div>
          <div className={cardBodyClass}>
            {upcoming.length === 0 ? (
              <p className="m-0 text-fluid-sm text-ink-500">
                {t("Dashboard.Overview.noUpcoming")}
              </p>
            ) : (
              <ul className="m-0 flex list-none flex-col gap-3 p-0">
                {upcoming.slice(0, 5).map((b) => (
                  <li
                    key={b.id}
                    className="flex items-center justify-between gap-3 border-b border-ink-100 pb-3 last:border-0 last:pb-0"
                  >
                    <div className="min-w-0">
                      <p className="m-0 truncate text-fluid-sm font-medium text-ink-900">
                        {b.serviceName}
                      </p>
                      <p className="m-0 truncate text-xs text-ink-500">
                        {formatDate(b.bookingDateTime, i18n.language)} ·{" "}
                        {formatTime(b.bookingDateTime, i18n.language)}
                      </p>
                    </div>
                    <span
                      className={`${badgeClass} shrink-0 ${statusBadgeClass(b.status)}`}
                    >
                      {t(
                        `Dashboard.MyBookings.status.${b.status?.toLowerCase()}`,
                        { defaultValue: b.status },
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerOverview;
